import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UtensilsCrossed, LogIn } from "lucide-react";

const formatApiError = (detail) => {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const RestaurantAdminLogin = () => {
  const { login, admin, checking } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (checking) return <div className="min-h-[50vh] flex items-center justify-center text-navy/50">Loading…</div>;
  if (admin) {
    return <Navigate to={admin.role === "restaurant" ? "/restaurant-admin" : "/admin"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      if (u?.role === "rider") nav("/rider", { replace: true });
      else if (u?.role === "restaurant") nav("/restaurant-admin", { replace: true });
      else nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl border border-navy/10 shadow-xl p-8">
        <div className="w-12 h-12 rounded-2xl bg-teal/15 flex items-center justify-center mb-4">
          <UtensilsCrossed className="w-6 h-6 text-teal" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-navy tracking-tight">Restaurant sign in</h1>
        <p className="text-navy/60 text-sm mt-1">Manage your menu, hours and orders.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="restaurant-login-email" autoComplete="username" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="restaurant-login-password" autoComplete="current-password" />
          </div>
          <Button type="submit" disabled={busy} className="btn-coral w-full h-11" data-testid="restaurant-login-submit">
            <LogIn className="w-4 h-4 mr-2" /> {busy ? "…" : "Sign in"}
          </Button>
        </form>
        <div className="mt-6 text-center text-xs text-navy/50 space-x-3">
          <Link to="/partner" className="hover:text-coral">Not a partner yet?</Link>
          <span>•</span>
          <Link to="/" className="hover:text-coral">Back to Caleta Eats</Link>
        </div>
      </div>
    </div>
  );
};

export default RestaurantAdminLogin;
